import type { ActionFieldSpec, ActionFieldValue } from "./ActionFieldEditor";

/**
 * Seed the live value map for an action form (HITL card / manual composer).
 *
 * Layered so an auto-filled required field is never reported empty:
 *   1. **BE-resolved schema defaults** (``spec.value``) — e.g. the account
 *      auto-selected when a single connection exists. These are resolved at
 *      schema-build time, AFTER the AI proposal was flattened into
 *      ``proposedFields``, so they only live on the schema — not the proposal.
 *   2. **``proposedFields``** (what the AI proposed) override the defaults.
 *   3. The user's **``editedFields``** override everything.
 *
 * Blank/absent ``spec.value`` contributes nothing — with an empty schema-default
 * layer the result is byte-identical to the old ``{...proposedFields, ...edits}``.
 * Shared by ``ActionDrawer`` and ``ScalarActionProposalMount`` (DRY).
 */
export function seedActionValues(
  schema: readonly ActionFieldSpec[],
  proposedFields: Readonly<Record<string, ActionFieldValue>>,
  editedFields?: Readonly<Record<string, ActionFieldValue>>,
): Record<string, ActionFieldValue> {
  const defaults: Record<string, ActionFieldValue> = {};
  for (const s of schema) {
    if (s.value !== undefined && s.value !== null && s.value !== "") {
      defaults[s.name] = s.value;
    }
  }
  return { ...defaults, ...proposedFields, ...(editedFields ?? {}) };
}
